import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { last } from "lodash";
import TextTruncate from "components/TextTruncate";
import IconButton from "components/IconButton";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import LineTypeIndicator from "../../../LineTypeIndicator";
import { viewTypeOptions } from "../../../ProcessDataViewerElement/components/SourceRow";
import { prepareLineGraphData, preparePlotLayout } from "../../../GraphElementGraph/utils";
import { prepareErrorsGraphPlotLayout } from "../../../GraphElementErrorsGraph/utils";
import GraphElementErrorsGraph from "../../../GraphElementErrorsGraph";
import CalculationStatusIcon from "../../../CalculationStatusIcon";
import SourceName from "../../../SourceName";
import BiasedIcon from "../../../BiasedIcon";
import { parameterTypesSelector, processVariablesSelector } from "store/global/global.selectors";
import { CalculationStatusSource, ResultElementParameterType, ResultElementSourceAppearance, ResultElementSourceType, ResultSetElementType, SimulationRunState, } from "store/rs-workspace/rs-workspace.types";
import { SourceFields } from "../../../ProcessDataViewerElement/types";
import { workspaceCellModelsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { calculationElementRowSelector, calculationGraphElementSourceResultSelector, getActiveResultSetIdSelector, isResultElementChangingSelector, processDataViewerSourceFormValuesSelector, } from "store/rs-workspace/selectors/results.selectors";
import { getSimulationTimestampOptions, getSourceLineColor, getSourceNameTooltip, getSourceNameTooltipLabel, getTimingSummaryText, } from "store/rs-workspace/utils/results.utils";
import { setLineGraphSourceParams } from "store/rs-workspace/actions/results.actions";
import { isNotEmpty } from "utils/validate";
import { MAX_VIEWABLE_SOURCES } from "../Sources";
import { INPUT } from "shared/labels";
import { TimeUnits } from "shared/types";
import { getTotalRuns } from "store/rs-workspace/utils/calculations.utils";
import { getCombinationParameterPreview } from "store/rs-workspace/utils/common.utils";
import { usePrevious } from "hooks/usePrevious";
import { getTimeUnit } from "utils/dateTime";
import IcNotVisible from "icons/icn-not-visible.svg";
import IcVisible from "icons/icn-visible.svg";
import AutoscaleIcon from "icons/autoscale.svg";
import LockIcon from "icons/lock.svg";
import UnlockIcon from "icons/open-lock.svg";
import * as styles from "../../styles.module.scss";
const SourceRowPreview = ({ elementData, source, sources, axisName, index, rangeConfig: { autorange, fixedrange } = {}, alternativeFlowsMapping, handleAutoscaleButtonClick, onLockButtonClick, }) => {
    const dispatch = useDispatch();
    const [errorsGraphOpen, setErrorsGraphOpen] = useState(false);
    const [errorsGraphData, setErrorsGraphData] = useState(null);
    const [errorsPlotLayout, setErrorsPlotLayout] = useState(null);
    const [timeUnit, setTimeUnit] = useState(TimeUnits.hours);
    const simulations = useSelector(workspaceSimulationsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const processVariables = useSelector(processVariablesSelector);
    const resultSetId = useSelector(getActiveResultSetIdSelector);
    const parameterTypes = useSelector(parameterTypesSelector);
    const isResultElementChanging = useSelector(isResultElementChangingSelector(resultSetId, elementData.id));
    const { preparedSource, isCompleted, isFilled, hasNoParameters, sourceTypeLabel } = useSelector(processDataViewerSourceFormValuesSelector(source, elementData.parameter));
    const sourceResult = useSelector(calculationGraphElementSourceResultSelector({ resultSetId, resultSetElementId: elementData.id }, elementData.type, source.id));
    const sourceCalculationRow = useSelector(calculationElementRowSelector({ resultSetId, resultSetElementId: elementData.id }, source.id));
    const prevIsResultElementChanging = usePrevious(isResultElementChanging);
    const xAxisIsDiscrete = useMemo(() => elementData.parameter.parameter?.type === ResultElementParameterType.ALTERNATIVE_FLOW, [elementData.parameter.parameter?.type]);
    const isInsight = useMemo(() => preparedSource?.source?.type === ResultElementSourceType.Insight, [preparedSource?.source]);
    const timestampOptions = useMemo(() => getSimulationTimestampOptions(source?.source?.simulationId ?? 0, simulations, recipes), [recipes, simulations, source?.source]);
    const customVariablesList = useMemo(() => {
        const { simulationId } = { ...source?.source };
        const recipeId = simulations.find(({ id }) => id === simulationId)?.recipeId;
        const recipe = recipes.find(({ id }) => id === recipeId);
        return recipe?.state?.customVariables?.filter(({ recordInData }) => Boolean(recordInData)) ?? [];
    }, [recipes, simulations, source]);
    const timingSummaryText = useMemo(() => getTimingSummaryText(source?.source?.timing ?? null, timestampOptions, processVariables, customVariablesList), [timestampOptions, processVariables, customVariablesList, source?.source]);
    const sourceSubtitle = useMemo(() => {
        const variable = preparedSource?.source?._variableName?.label;
        const units = preparedSource?.source?.units?.label;
        return variable && `${variable} ${units ? `(${units})` : ""}`;
    }, [preparedSource?.source]);
    const turnOnSourceViewDisabled = useMemo(() => !preparedSource?.view && sources.filter(({ view }) => Boolean(view))?.length >= MAX_VIEWABLE_SOURCES, [sources, preparedSource?.view]);
    const sourceSelected = useMemo(() => preparedSource?.source?._sourceName, [preparedSource]);
    const appearance = useMemo(() => {
        const { state, points, fetching } = { ...sourceResult };
        const calculationStatus = sourceCalculationRow.status;
        if (!isCompleted) {
            return ResultElementSourceAppearance.Incomplete;
        }
        if (fetching || calculationStatus === CalculationStatusSource.InProgress) {
            return ResultElementSourceAppearance.InProgress;
        }
        if (calculationStatus === CalculationStatusSource.Paused) {
            return ResultElementSourceAppearance.Paused;
        }
        if (state === SimulationRunState.ERROR) {
            return ResultElementSourceAppearance.Error;
        }
        if (!!points?.length || state === SimulationRunState.FINISHED) {
            return ResultElementSourceAppearance.Calculated;
        }
        return ResultElementSourceAppearance.Ready;
    }, [sourceResult, sourceCalculationRow.status, isCompleted]);
    const tooltip = useMemo(() => {
        const tooltipLabel = getSourceNameTooltipLabel(sourceTypeLabel, preparedSource?.source, appearance, simulations, cellModels, recipes);
        return getSourceNameTooltip(appearance, tooltipLabel, sourceResult?.error, hasNoParameters, isFilled);
    }, [
        sourceTypeLabel,
        preparedSource?.source,
        appearance,
        simulations,
        cellModels,
        recipes,
        sourceResult?.error,
        hasNoParameters,
        isFilled,
    ]);
    const onChangeSourceParams = useCallback((option, fieldName) => {
        if (!resultSetId || !source?.id)
            return;
        dispatch(setLineGraphSourceParams({
            resultSetId,
            elementId: elementData.id,
            sourceId: source?.id,
            lineType: source?.lineType,
            view: source?.view,
            viewType: source?.viewType,
            color: source?.color,
            [fieldName]: option?.value,
        }));
    }, [dispatch, elementData.id, resultSetId, source]);
    useEffect(() => {
        const { state, points, fetching, accuracy } = { ...sourceResult };
        const calculationStatus = sourceCalculationRow.status;
        if (sourceResult === undefined ||
            points?.length === 0 ||
            state !== SimulationRunState.FINISHED ||
            fetching ||
            calculationStatus === CalculationStatusSource.InProgress) {
            return;
        }
        if (errorsGraphData === null || (prevIsResultElementChanging && !isResultElementChanging)) {
            const totalRunsCount = accuracy
                ? getTotalRuns({
                    ...accuracy,
                    numberOfOptimisationSteps: null,
                }, false)
                : 0;
            const preparedParameter = getCombinationParameterPreview(elementData.parameter, recipes, cellModels, parameterTypes);
            const parameterUnit = preparedParameter.parameter?.type === ResultElementParameterType.ALTERNATIVE_FLOW
                ? ""
                : preparedParameter?.parameter?.units;
            setErrorsGraphData(prepareLineGraphData(sourceResult ? [sourceResult] : [], source.id, totalRunsCount, alternativeFlowsMapping));
            setTimeUnit(getTimeUnit(last(points)?.point?.x ?? 1));
            const plotLayout = preparePlotLayout(sourceResult ? [sourceResult] : [], elementData.xPlotConfig, undefined, {
                xaxis: {
                    title: `${preparedParameter.parameter?.name} ${parameterUnit ? `(${parameterUnit})` : ""}`,
                },
                yaxis: { fixedrange: false },
                yaxis2: { fixedrange: false },
            }, xAxisIsDiscrete, alternativeFlowsMapping);
            const errorsPlotLayout = prepareErrorsGraphPlotLayout(sourceResult, ResultSetElementType.LineGraph, totalRunsCount);
            setErrorsPlotLayout({
                ...plotLayout,
                ...errorsPlotLayout,
                shapes: [...(plotLayout.shapes ?? []), ...(errorsPlotLayout.shapes ?? [])],
            });
        }
    }, [
        alternativeFlowsMapping,
        cellModels,
        elementData.parameter,
        elementData.xPlotConfig,
        errorsGraphData,
        isResultElementChanging,
        parameterTypes,
        prevIsResultElementChanging,
        recipes,
        source.id,
        sourceCalculationRow.status,
        sourceResult,
        xAxisIsDiscrete,
    ]);
    return (React.createElement("div", { className: classNames(styles.row, {
            [styles.faded]: !preparedSource.view,
            [styles.errored]: !!sourceResult?.error && appearance === ResultElementSourceAppearance.Error,
        }) },
        React.createElement("div", { className: classNames(styles.cell, styles.view) },
            React.createElement(IconButton, { tabIndex: -1, title: "View", disabled: turnOnSourceViewDisabled, classes: { root: styles.iconButton }, icon: preparedSource?.view ? React.createElement(IcVisible, null) : React.createElement(IcNotVisible, null), onClick: () => !turnOnSourceViewDisabled &&
                    onChangeSourceParams({ value: !preparedSource?.view, label: "" }, SourceFields.view) })),
        React.createElement(SourceName, { className: classNames(styles.cell, styles.source), tooltip: sourceResult?.error || tooltip },
            React.createElement("div", { className: styles["source-container"] },
                React.createElement(CalculationStatusIcon, { accuracy: sourceResult?.accuracy || null, appearance: appearance, className: styles["status-icon"] }),
                React.createElement("div", null,
                    React.createElement(TextTruncate, { text: preparedSource?.source?._sourceName || INPUT.UNDEFINED, className: classNames(styles["preview-value"], styles.regularHeight, {
                            [styles.accented]: appearance === ResultElementSourceAppearance.Error ||
                                appearance === ResultElementSourceAppearance.Incomplete,
                        }) }),
                    !elementData.fullView && !isInsight && isNotEmpty(preparedSource?.source?._sourceName) && (React.createElement(TextTruncate, { text: sourceSubtitle || INPUT.UNDEFINED, className: styles.subtitle })),
                    !elementData.fullView &&
                        !isInsight &&
                        sourceSelected &&
                        isNotEmpty(preparedSource?.source?._sourceName) && (React.createElement(TextTruncate, { text: timingSummaryText || INPUT.UNDEFINED, className: classNames(styles["preview-value"], styles.timingText) }))))),
        elementData.fullView && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: classNames(styles.cell, styles.variable) }, !isInsight && isNotEmpty(preparedSource?.source?._variableName) && (React.createElement(TextTruncate, { text: preparedSource?.source?._variableName?.label || INPUT.UNDEFINED, className: styles["preview-value"] }))),
            React.createElement("div", { className: classNames(styles.cell, styles.timing) },
                React.createElement(TextTruncate, { text: timingSummaryText || (!isInsight && INPUT.UNDEFINED), className: classNames(styles["preview-value"], styles.fullTimingText) })))),
        React.createElement("div", { className: classNames(styles.cell, styles.line) },
            React.createElement(LineTypeIndicator, { color: getSourceLineColor(preparedSource?.color, index), onChange: (color) => onChangeSourceParams({ value: color, label: "" }, SourceFields.color) })),
        React.createElement("div", { className: classNames(styles.cell, styles.viewType) },
            React.createElement(SelectInput, { items: viewTypeOptions, itemRenderer: SelectItemRenderer, className: styles.select, placeholder: "Select", value: preparedSource?.viewType, onChange: (option) => onChangeSourceParams(option, SourceFields.viewType) })),
        React.createElement("div", { className: classNames(styles.cell, styles.autoscale) },
            React.createElement("div", { className: styles["icon-container"] }, !!sourceResult?.hasErrors && (React.createElement(BiasedIcon, { errorsPlotLayout: errorsPlotLayout, firstError: sourceResult?.errors?.[0], setErrorsPlotLayout: setErrorsPlotLayout, onOpen: setErrorsGraphOpen, sourceView: !!source?.view }))),
            React.createElement("div", { className: styles["icon-container"] },
                React.createElement(IconButton, { icon: React.createElement(AutoscaleIcon, null), title: "Autoscale", classes: { root: classNames(styles.scaleIcon, { [styles.iconfill]: autorange }) }, onClick: () => handleAutoscaleButtonClick && handleAutoscaleButtonClick(axisName, source.id) })),
            React.createElement("div", { className: styles["icon-container"] },
                React.createElement(IconButton, { tabIndex: -1, classes: { root: styles.iconButton }, onClick: () => onLockButtonClick && onLockButtonClick(!fixedrange, axisName, source.id), icon: fixedrange ? React.createElement(LockIcon, null) : React.createElement(UnlockIcon, null), title: fixedrange ? "Unlock axis" : "Lock axis", disabled: !source.view }))),
        errorsPlotLayout !== null && (React.createElement(GraphElementErrorsGraph, { isOpen: errorsGraphOpen, onClose: () => setErrorsGraphOpen(false), preparedSource: preparedSource, elementData: elementData, graphData: errorsGraphData ?? [], plotLayout: errorsPlotLayout, timeUnit: timeUnit, error: sourceResult?.errors[0], isDiscreteGraph: xAxisIsDiscrete, discreteXTicksMapping: alternativeFlowsMapping }))));
};
export default SourceRowPreview;
